import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  approveAssignments, countPendingApprovals, listAllProposals, type PendingProposal,
} from '../../api/assignments';
// `api/payroll.ts` (Task 32) todavía no existe: hasta entonces la pestaña "Cierres" usa la
// consulta directa equivalente sobre `payroll_closures` (tabla creada en la Task 3).
import { supabase } from '../../lib/supabaseClient';
import { listDocumentsWithAlerts } from '../../api/documents';
import type { DocumentAlert } from '../../domain/documents';
import FilaTarjeta from '../../components/FilaTarjeta';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';

interface ClosureListItem {
  id: string;
  period_start: string;
  period_end: string;
  status: 'borrador' | 'aprobado';
  created_at: string;
  approved_at: string | null;
  campaign: { id: string; client: string; brand: string } | null;
}

type Tab = 'asignaciones' | 'solicitudes' | 'cierres';

/**
 * `soloJefe` marca las solapas que NO son del coordinador.
 *
 * El coordinador entra a esta pantalla (2026-08-12) para aprobar las propuestas que suben sus
 * supervisores. Pero acá también viven **crear cuentas de trabajadores** y **aprobar cierres de
 * pago**: dos cosas de otro nivel, que el servidor además le va a rechazar. Mostrarle solapas
 * que solo le devuelven un muro es la misma trampa que un botón que el servidor rechaza.
 */
const TAB_LABELS: { key: Tab; label: string; soloJefe?: true }[] = [
  { key: 'asignaciones', label: 'Asignaciones' },
  { key: 'solicitudes', label: 'Solicitudes de registro', soloJefe: true },
  { key: 'cierres', label: 'Cierres', soloJefe: true },
];

const hhmm = (t: string) => t.slice(0, 5);

export default function Aprobaciones() {
  const [tab, setTab] = useState<Tab>('asignaciones');
  const { roles } = useAuth();
  const esJefe = roles.includes('admin');
  const solapas = TAB_LABELS.filter((t) => esJefe || !t.soloJefe);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Aprobaciones</h1>
      <div className="flex flex-wrap gap-2 border-b mb-4">
        {solapas.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 -mb-px border-b-2 ${
              tab === key
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-gray-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'asignaciones' && <BandejaAsignaciones />}
      {/* La comprobación se repite acá a propósito: esconder el botón no alcanza si mañana
          alguien deja el estado en otra solapa por cualquier camino. */}
      {tab === 'solicitudes' && esJefe && <SolicitudesRegistro />}
      {tab === 'cierres' && esJefe && <CierresPendientes />}
    </div>
  );
}

function SolicitudesRegistro() {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    countPendingApprovals()
      .then(({ applications }) => setCount(applications))
      .catch(() => setCount(0));
  }, []);

  return (
    <div>
      <p className="mb-3">
        {count === null
          ? 'Cargando…'
          : count === 0
            ? 'No hay solicitudes de registro pendientes.'
            : `Hay ${count} solicitud(es) de registro pendiente(s).`}
      </p>
      <Link to="/panel/trabajadores" className="text-blue-600 underline">
        Ir a Trabajadores para revisarlas
      </Link>
    </div>
  );
}

/** Cierres de pago en `borrador` esperando la aprobación del jefe (spec §7). */
function CierresPendientes() {
  const [closures, setClosures] = useState<ClosureListItem[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase
      .from('payroll_closures')
      .select('id, period_start, period_end, status, created_at, approved_at, campaign:campaigns(id, client, brand)')
      .eq('status', 'borrador')
      .order('created_at', { ascending: false })
      .then(({ data, error: err }) => {
        if (err) {
          setError(err.message);
          setClosures([]);
          return;
        }
        setClosures((data ?? []) as unknown as ClosureListItem[]);
      });
  }, []);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (closures === null) return <p className="text-gray-500">Cargando cierres…</p>;
  if (closures.length === 0) {
    return <p className="text-gray-500">No hay cierres pendientes de aprobación.</p>;
  }

  return (
    <div>
      <p className="mb-3 text-sm text-gray-600">
        {closures.length} cierre(s) en borrador. La aprobación se hace en el detalle del
        cierre: una vez aprobado es inmutable.
      </p>
      {/* En celular: una tarjeta por cierre (sin desplazamiento horizontal). */}
      <div className="space-y-2 lg:hidden">
        {closures.map((c) => (
          <FilaTarjeta
            key={c.id}
            titulo={c.campaign ? `${c.campaign.client} — ${c.campaign.brand}` : '—'}
            campos={[
              { etiqueta: 'Periodo', valor: `${c.period_start} → ${c.period_end}` },
              { etiqueta: 'Creado', valor: c.created_at.slice(0, 10) },
            ]}
            acciones={
              <Link to="/panel/cierres" className="text-blue-600 underline">
                Ver detalle
              </Link>
            }
          />
        ))}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full text-sm border rounded">
          <thead>
            <tr className="text-left text-gray-500 bg-gray-50">
              <th className="px-3 py-2">Campaña</th>
              <th className="px-3 py-2">Periodo</th>
              <th className="px-3 py-2">Creado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {closures.map((c) => (
              <tr key={c.id} className="border-t">
                <td className="px-3 py-2">
                  {c.campaign ? `${c.campaign.client} — ${c.campaign.brand}` : '—'}
                </td>
                <td className="px-3 py-2">{c.period_start} → {c.period_end}</td>
                <td className="px-3 py-2">{c.created_at.slice(0, 10)}</td>
                <td className="px-3 py-2 text-right">
                  <Link to="/panel/cierres" className="text-blue-600 underline">
                    Ver detalle
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BandejaAsignaciones() {
  const [proposals, setProposals] = useState<PendingProposal[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { roles } = useAuth();
  /**
   * Aprobar y rechazar es del coordinador y del jefe; `datos` entra en lectura (10-set).
   *
   * Le sirve para saber qué turnos todavía NO están confirmados —eso es información de revisión—,
   * pero `approve_assignments` solo acepta al coordinador DESIGNADO de la campaña, así que los
   * botones y las casillas de selección no se le dibujan. Sin las casillas la bandeja se lee como
   * una lista, que es lo que es para él.
   */
  const puedeAccionar = puedeEditar('aprobaciones', roles);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [docAlerts, setDocAlerts] = useState<Map<string, DocumentAlert[]>>(new Map());

  useEffect(() => {
    let active = true;
    listDocumentsWithAlerts()
      .then((rows) => {
        if (!active) return;
        const m = new Map<string, DocumentAlert[]>();
        for (const r of rows) if (r.alerts.length > 0) m.set(r.worker_id, r.alerts);
        setDocAlerts(m);
      })
      .catch(() => setDocAlerts(new Map()));
    return () => { active = false; };
  }, []);

  const reload = useCallback(() => {
    listAllProposals()
      .then((rows) => { setProposals(rows); setSelected(new Set()); })
      .catch((e) => setMessage(e instanceof Error ? e.message : 'Error al cargar propuestas'));
  }, []);

  useEffect(reload, [reload]);

  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: PendingProposal[] }>();
    for (const p of proposals) {
      const entry = map.get(p.positions.campaign_id);
      if (entry) {
        entry.items.push(p);
      } else {
        map.set(p.positions.campaign_id, {
          label: `${p.positions.campaigns.client} — ${p.positions.campaigns.brand}`,
          items: [p],
        });
      }
    }
    // Dentro de cada campaña, los reemplazos primero (aprobación rápida, spec §7).
    for (const g of map.values()) {
      g.items.sort((x, y) => Number(y.is_replacement) - Number(x.is_replacement));
    }
    return [...map.entries()].map(([campaignId, g]) => ({ campaignId, ...g }));
  }, [proposals]);

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleCampaign(items: PendingProposal[], checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of items) {
        if (checked) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });
  }

  async function act(approve: boolean) {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    setMessage('');
    try {
      const n = await approveAssignments(ids, approve);
      setMessage(approve ? `${n} asignación(es) aprobada(s)` : `${n} asignación(es) rechazada(s)`);
      reload();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Error al procesar la acción');
    } finally {
      setBusy(false);
    }
  }

  if (proposals.length === 0) {
    return <p className="text-gray-500">{message || 'No hay asignaciones pendientes de aprobación.'}</p>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {puedeAccionar && (<>
        <button
          onClick={() => act(true)}
          disabled={busy || selected.size === 0}
          className="px-4 py-2 rounded bg-green-600 text-white disabled:opacity-50"
        >
          Aprobar seleccionadas ({selected.size})
        </button>
        <button
          onClick={() => act(false)}
          disabled={busy || selected.size === 0}
          className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
        >
          Rechazar seleccionadas ({selected.size})
        </button>
        </>)}
        {message && <span className="text-sm text-gray-700">{message}</span>}
      </div>
      {groups.map((g) => {
        const allChecked = g.items.every((p) => selected.has(p.id));
        return (
          <div key={g.campaignId} className="mb-6 border rounded">
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 border-b">
              {puedeAccionar && (
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => toggleCampaign(g.items, e.target.checked)}
                />
              )}
              <span className="font-medium">{g.label}</span>
              <span className="text-sm text-gray-500">— todas las de esta campaña</span>
            </div>
            {/* En celular: una tarjeta por propuesta, con su checkbox de selección arriba. */}
            <div className="space-y-2 p-2 lg:hidden">
              {g.items.map((p) => (
                <FilaTarjeta
                  key={p.id}
                  titulo={`${p.worker.first_names} ${p.worker.last_names}`}
                  seleccion={!puedeAccionar ? undefined : (
                    <label className="flex min-h-[44px] min-w-[44px] items-center justify-center">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar asignación"
                        checked={selected.has(p.id)}
                        onChange={() => toggleOne(p.id)}
                      />
                    </label>
                  )}
                  estados={
                    <>
                      {p.is_replacement && (
                        <span
                          title="Esta posición tuvo un reemplazo: aprobación rápida"
                          className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5"
                        >
                          Reemplazo
                        </span>
                      )}
                      {/* Informativo: advierte de documentos vencidos/por vencer, NO bloquea la aprobación. */}
                      {(docAlerts.get(p.worker.id) ?? []).map((a) => (
                        <span
                          key={`${a.type}-${a.severity}`}
                          className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                        >
                          ⚠ {a.message}
                        </span>
                      ))}
                    </>
                  }
                  campos={[
                    { etiqueta: 'DNI', valor: p.worker.dni },
                    { etiqueta: 'Fecha', valor: p.positions.date },
                    { etiqueta: 'PDV', valor: p.positions.pdv.name },
                    {
                      etiqueta: 'Horario',
                      valor: `${hhmm(p.positions.start_time)}–${hhmm(p.positions.end_time)}`,
                    },
                  ]}
                />
              ))}
            </div>

            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">Trabajador</th>
                    <th className="px-3 py-2">DNI</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">PDV</th>
                    <th className="px-3 py-2">Horario</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggleOne(p.id)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div>
                          {p.worker.first_names} {p.worker.last_names}
                          {p.is_replacement && (
                            <span
                              title="Esta posición tuvo un reemplazo: aprobación rápida"
                              className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5"
                            >
                              Reemplazo
                            </span>
                          )}
                        </div>
                        {/* Informativo: advierte de documentos vencidos/por vencer, NO bloquea la aprobación. */}
                        {(docAlerts.get(p.worker.id) ?? []).map((a) => (
                          <div
                            key={`${a.type}-${a.severity}`}
                            className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                          >
                            ⚠ {a.message}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2">{p.worker.dni}</td>
                      <td className="px-3 py-2">{p.positions.date}</td>
                      <td className="px-3 py-2">{p.positions.pdv.name}</td>
                      <td className="px-3 py-2">{hhmm(p.positions.start_time)}–{hhmm(p.positions.end_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
